import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAckTeamCommunicationsMutation } from '../api';
import { useCommunications } from '../CommunicationsContext';
import { selectCommunicationsPayload } from '../communicationsSlice';
import { COMMUNICATIONS_POPUP_REPEAT } from '../constants';
import { CommunicationsScreenKey } from '../communications.model';
import { shouldShowCommunicationsPopup } from '../utils';
import CommunicationStrip from './CommunicationStrip';
import CommunicationPopup from './CommunicationPopup';

const CommunicationsFrame: FC = () => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const payload = useAppSelector(selectCommunicationsPayload);
  const { screenKey, strip, popup, handleCta } = useCommunications();
  const [ackTeamCommunications] = useAckTeamCommunicationsMutation();
  const handledPopupKeyRef = useRef<string | null>(null);
  const prevScreenKeyRef = useRef<CommunicationsScreenKey | null | undefined>(undefined);
  const consumedHomeVisitIdRef = useRef(0);
  const [homeVisitId, setHomeVisitId] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openPopupKey, setOpenPopupKey] = useState<string | null>(null);

  const teamId = currentTeam?.id ?? null;
  const hasTeamPayload = teamId != null && payload?.teamId === teamId;
  const payloadChangeId = hasTeamPayload ? payload?.changeId ?? null : null;
  // Include response changeId so a new communications payload is treated as a new popup opportunity.
  const popupKey =
    popup && teamId != null ? `${teamId}:${payloadChangeId ?? 'none'}:${popup.id}:${popup.changeId}` : null;
  const eligibleToShow = shouldShowCommunicationsPopup(teamId, popup) && handledPopupKeyRef.current !== popupKey;
  const isHome = screenKey === 'HOME';
  const isHomeVisitArmed = isHome && homeVisitId > 0 && consumedHomeVisitIdRef.current !== homeVisitId;

  // Arm a new home visit when entering home (nav from another tab) or on relaunch mount.
  // While already on home, payload updates must not open a popup mid-task.
  useEffect(() => {
    const prev = prevScreenKeyRef.current;
    if (screenKey === 'HOME' && prev !== 'HOME') {
      setHomeVisitId(visitId => visitId + 1);
    }
    prevScreenKeyRef.current = screenKey;
  }, [screenKey]);

  useEffect(() => {
    if (!eligibleToShow || !popupKey) {
      if (openPopupKey && openPopupKey !== popupKey) {
        setIsPopupOpen(false);
        setOpenPopupKey(null);
      }
      // First payload for this home visit had nothing to show — consume the visit so later
      // updates wait for the next time the merchant enters home.
      if (isHome && isHomeVisitArmed && hasTeamPayload && !eligibleToShow) {
        consumedHomeVisitIdRef.current = homeVisitId;
      }
      return;
    }

    if (isHome && !isHomeVisitArmed) {
      return;
    }

    if (openPopupKey === popupKey && isPopupOpen) return;

    setOpenPopupKey(popupKey);
    setIsPopupOpen(true);
    if (isHome) {
      consumedHomeVisitIdRef.current = homeVisitId;
    }
  }, [eligibleToShow, popupKey, openPopupKey, isPopupOpen, isHome, isHomeVisitArmed, hasTeamPayload, homeVisitId]);

  const handlePopupClose = () => {
    if (popup && teamId != null && popupKey) {
      handledPopupKeyRef.current = popupKey;
      const repeat = popup.repeat ?? COMMUNICATIONS_POPUP_REPEAT.REPEATED;
      if (repeat === COMMUNICATIONS_POPUP_REPEAT.ONCE) {
        ackTeamCommunications({
          teamId,
          body: { messageId: popup.id, changeId: popup.changeId }
        });
      }
    }
    setIsPopupOpen(false);
  };

  const handlePopupCta = () => {
    if (!popup) return;
    handleCta(popup);
  };

  const handleStripCta = () => {
    if (!strip) return;
    handleCta(strip);
  };

  return (
    <>
      {strip ? <CommunicationStrip message={strip} onCtaClick={handleStripCta} /> : null}
      {isPopupOpen && popup ? (
        <CommunicationPopup open={true} message={popup} onClose={handlePopupClose} onCtaClick={handlePopupCta} />
      ) : null}
    </>
  );
};

export default CommunicationsFrame;
